import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Alert,
  Input,
  InputGroup,
  ButtonGroup,
} from 'reactstrap';
import axios from 'axios';
import Select from 'react-select';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export const MEDICATIONS = [
  'Lasix', 'Bumex', 'Demadex', 'Midamor', 'Hydro-ride', 'Esidrix', 'Hydrodiuril',
  'Lozol', 'Dyrenium', 'Thalitone', 'Aldactone', 'Inspra', 'Tenormin', 'Zebeta',
  'Coreg', 'Coreg CR', 'Trandate', 'Toprol XL', 'Kapspargo Sprinkle', 'Lopressor',
  'Corgard', 'Bystolic', 'Visken', 'Inderal', 'Inderal LA', 'InnoPran XL', 'Capoten',
  'Vasotec', 'Monopril', 'Prinivil', 'Zestril', 'Aceon', 'Accupril', 'Altace',
  'Mavik', 'Lotensin', 'Univasc', 'Atacand', 'Cozaar', 'Diovan', 'Avapro',
  'Benicar', 'Micardis', 'Norvasc', 'Cardizem CD', 'Cardizem SR', 'Dilacor XR',
  'Tiazac', 'Plendil', 'Cardene SR', 'Adalat CC', 'Procardia XL', 'Calan SR',
  'Covera HS', 'Isoptin SR', 'Verelan', 'Cardura', 'Minipress', 'Hytrin',
  'Aldomet', 'Catapres', 'Duraclon', 'Kapvay', 'Nexiclon XR', 'Intuniv',
  'Tenex', 'Apresoline', 'Loniten'
].map(med => ({ value: med.toLowerCase(), label: med }));

const TimeInput = ({ value, onChange }) => {
  const [hours, setHours] = useState('12');
  const [minutes, setMinutes] = useState('00');
  const [period, setPeriod] = useState('AM');

  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':');
      const hour = parseInt(h);
      setHours(hour === 0 ? '12' : (hour > 12 ? (hour - 12).toString() : hour.toString()));
      setMinutes(m);
      setPeriod(hour >= 12 ? 'PM' : 'AM');
    }
  }, [value]);

  const handleHourChange = (e) => {
    let val = e.target.value;
    if (val === '') val = '12';
    val = Math.max(1, Math.min(12, parseInt(val) || 12)).toString();
    setHours(val);
    updateTime(val, minutes, period);
  };

  const handleMinuteChange = (e) => {
    let val = e.target.value;
    if (val === '') val = '00';
    val = Math.max(0, Math.min(59, parseInt(val) || 0)).toString().padStart(2, '0');
    setMinutes(val);
    updateTime(hours, val, period);
  };

  const togglePeriod = (newPeriod) => {
    setPeriod(newPeriod);
    updateTime(hours, minutes, newPeriod);
  };

  const updateTime = (h, m, p) => {
    let militaryHour = parseInt(h);
    if (p === 'PM' && militaryHour !== 12) militaryHour += 12;
    if (p === 'AM' && militaryHour === 12) militaryHour = 0;
    const timeStr = `${militaryHour.toString().padStart(2, '0')}:${m}`;
    onChange(timeStr);
    console.log('Updated time:', timeStr); // For debugging
  };


  return (
    <div className="d-flex align-items-center gap-2">
      <InputGroup style={{ width: 'auto' }}>
        <Input
          type="number"
          value={hours}
          onChange={handleHourChange}
          min="1"
          max="12"
          style={{ width: '60px', textAlign: 'center' }}
          onBlur={() => {
            setHours(hours.padStart(2, '0'));
          }}
        />
        <span className="input-group-text">:</span>
        <Input
          type="number"
          value={minutes}
          onChange={handleMinuteChange}
          min="0"
          max="59"
          style={{ width: '60px', textAlign: 'center' }}
          onBlur={() => {
            setMinutes(minutes.padStart(2, '0'));
          }}
        />
      </InputGroup>
      <ButtonGroup>
        <Button
          color={period === 'AM' ? 'primary' : 'secondary'}
          onClick={() => togglePeriod('AM')}
          outline={period !== 'AM'}
        >
          AM
        </Button>
        <Button
          color={period === 'PM' ? 'primary' : 'secondary'}
          onClick={() => togglePeriod('PM')}
          outline={period !== 'PM'}
        >
          PM
        </Button>
      </ButtonGroup>
    </div>
  );
};

const ImportantMedicationModal = ({ isOpen, toggle, subjectId,selectedSubject }) => {
  const [selectedMedication, setSelectedMedication] = useState('');
  const [medicationTime, setMedicationTime] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [subjectData, setSubjectData] = useState(null);


 


  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress);
      const now = new Date();
      setMedicationTime(`${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`);
    
    }
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isOpen, handleKeyPress]);


  useEffect(() => {
    if (isOpen  || selectedSubject) {
      setSubjectData(selectedSubject);
      const concomitantMeds = subjectData?.concomitantMedication || [];
      if (concomitantMeds.length > 0) {
        console.log('Concomitant meds:', concomitantMeds); // For debugging
        const sortedMeds = [...concomitantMeds].sort((a, b) => {
          return (b.startDate || 0) - (a.startDate || 0);
        });

        console.log('Sorted meds:', sortedMeds); // For debugging
  
        const matchingMed = sortedMeds.find(med => 
          MEDICATIONS.some(m => m.value === med.medication.toLowerCase())
        );
        console.log('Matching med:', matchingMed); // For debugging

        if (matchingMed) {
          setSelectedMedication(matchingMed.medication.toLowerCase());
        }
      }
    }
  }, [isOpen, selectedSubject]);

  const resetForm = () => {
    setSelectedMedication('');
    setMedicationTime('');
    setError('');
    setSuccess(false);
    setIsLoading(false);
    setSubjectData(null);
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    if (!selectedMedication || !medicationTime) {
      setError('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      console.log('Selected time:', medicationTime); // For debugging
      
      // Parse the time in 24-hour format
      const [hoursStr, minutes] = medicationTime.split(':');
      const hours = parseInt(hoursStr);
      
      // Get current date
      const now = new Date();
      // Create date object with today's date and selected time
      const localDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hours,
        parseInt(minutes),
        0,
        0
      );

      // Convert to UTC timestamp in seconds
      const utcTimestamp = Math.floor(localDate.getTime() / 1000);
      console.log('UTC Timestamp:', utcTimestamp, new Date(utcTimestamp * 1000).toISOString()); // For debugging

      const response = await axios.post(`${BASE_URL}web/medicationTaken`, {
        subjectId: subjectId,
        medication: selectedMedication.toLowerCase(),
        medicationTime: utcTimestamp
      });
  

      setSuccess(true);
      setTimeout(() => {
        toggle();
      }, 1500);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to submit medication data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log('Selected medication:', selectedMedication); // For debugging
  }, [selectedMedication]);

  const selectedMedicationOption = selectedMedication ? 
  { value: selectedMedication, label: MEDICATIONS.find(m => m.value === selectedMedication)?.label || selectedMedication } : 
  null;

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Important Medication Taken</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}
        {success && <Alert color="success">Medication record saved successfully!</Alert>}
        {!success && (
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label for="medication">Medication *</Label>
              <Select
              required
                value={selectedMedicationOption}
                onChange={(option) => setSelectedMedication(option ? option.value : '')}
                options={MEDICATIONS}
                isClearable
                isSearchable
                placeholder="Search or enter medication..."
                allowCreateWhileLoading={true}
                createOptionPosition="first"
                formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
                noOptionsMessage={() => "Type to search or enter custom medication"}
                onInputChange={(inputValue, { action }) => {
                  if (action === 'input-change' && inputValue) {
                    const matchedMed = MEDICATIONS.find(med => 
                      med.value.toLowerCase() === inputValue.toLowerCase()
                    );
                    if (!matchedMed) {
                      setSelectedMedication(inputValue);
                    }
                  }
                }}
                isValidNewOption={(inputValue) => inputValue.length > 0}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '38px'
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999
                  })
                }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Time Taken *</Label>
              <TimeInput 
                value={medicationTime}
                onChange={setMedicationTime}
              />
            </FormGroup>
          </Form>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>Cancel</Button>
        {!success && (
          <Button 
            color="primary" 
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ImportantMedicationModal;